import { useRef, useState, useEffect } from "react";
import AvatarEditor from "react-avatar-editor";
import { useMediaQuery } from "@mui/material";
import {
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDescription,
  ContentWrapper,
  EditorContainer,
  SliderWrapper,
  SliderLabel,
  StyledSlider,
  StyledDialogActions,
  EDITOR_SIZE,
} from "./AvatarCropModal.styles";
import { CustomBtn, SecondaryBtn } from "../../Common/CustomBtn";

const AvatarCropModal = ({ open, image, onConfirm, onCancel }) => {
  const editorRef = useRef(null);
  const [scale, setScale] = useState(1.2);
  const [position, setPosition] = useState({ x: 0.5, y: 0.5 });
  const [imageSrc, setImageSrc] = useState(null);
  const smScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const editorSize = smScreen ? 280 : EDITOR_SIZE;

  useEffect(() => {
    if (!image) {
      setImageSrc(null);
      return;
    }
    setScale(1.2);
    setPosition({ x: 0.5, y: 0.5 });
    if (typeof image === "string") {
      setImageSrc(image);
      return;
    }
    const url = URL.createObjectURL(image);
    setImageSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [image]);

  const cropCanvasToFile = (canvas) => {
    const outputCanvas = document.createElement("canvas");
    outputCanvas.width = 1024;
    outputCanvas.height = 1024;
    const ctx = outputCanvas.getContext("2d");
    ctx.drawImage(
      canvas,
      0,
      0,
      canvas.width,
      canvas.height,
      0,
      0,
      1024,
      1024
    );
    return outputCanvas;
  };

  const handleConfirm = () => {
    const canvas = editorRef.current?.getImage();
    if (!canvas) return;

    const outputCanvas = cropCanvasToFile(canvas);
    outputCanvas.toBlob(
      (blob) => {
        if (blob) {
          onConfirm(new File([blob], "avatar.jpg", { type: "image/jpeg" }));
          onCancel();
        }
      },
      "image/jpeg",
      0.92
    );
  };

  const handleCancel = () => {
    setScale(1.2);
    setPosition({ x: 0.5, y: 0.5 });
    onCancel();
  };

  const handleScaleChange = (_, value) => setScale(value);

  if (!image || !imageSrc) return null;

  return (
    <StyledDialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <StyledDialogTitle>Crop your photo</StyledDialogTitle>

      <StyledDialogContent>
        <StyledDescription>
          Adjust the area to show exactly how your avatar will appear. Use the
          slider to zoom.
        </StyledDescription>

        <ContentWrapper>
          <EditorContainer $editorsize={editorSize}>
            <AvatarEditor
              ref={editorRef}
              image={imageSrc}
              width={editorSize}
              height={editorSize}
              border={0}
              borderRadius={0}
              color={[255, 255, 255, 0.6]}
              scale={scale}
              position={position}
              onPositionChange={setPosition}
            />
          </EditorContainer>

          <SliderWrapper>
            <SliderLabel>Zoom</SliderLabel>
            <StyledSlider
              value={scale}
              min={1}
              max={3}
              step={0.1}
              onChange={handleScaleChange}
            />
          </SliderWrapper>
        </ContentWrapper>
      </StyledDialogContent>

      <StyledDialogActions>
        <SecondaryBtn
          onClick={handleCancel}
          smallR
          text={"Cancel"}
          noIcon
          width={"35%"}
        />

        <CustomBtn
          smallR
          text={"Apply"}
          width={"35%"}
          onClick={handleConfirm}
        />
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default AvatarCropModal;
